import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, Home, Sparkles, ShoppingBag, Users, Smartphone, MessageCircle, UserCheck, Menu, X, BookOpen, Stars, User, LogOut, Bot, Flower, MoreHorizontal, Image, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';

const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [enabledTabs, setEnabledTabs] = useState([]);
  const [profileImageError, setProfileImageError] = useState(false);

  // Default navigation items (reordered with Luma first)
  const defaultNavItems = [
    { path: '/app', icon: Home, label: 'Home' },
    { path: '/app/journal', icon: BookOpen, label: 'Journaling' },
    { path: '/app/companion', icon: Bot, label: 'Companion' },
    { path: '/app/love-garden', icon: Flower, label: 'Garden' },
    { path: '/app/community', icon: MessageCircle, label: 'Community' },
    { path: '/app/more', icon: MoreHorizontal, label: 'More' }
  ];

  // Desktop navigation items (with Divine Guidance)
  const desktopNavItems = [
    { path: '/app', icon: Home, label: 'Home' },
    { path: '/app/journal', icon: BookOpen, label: 'Journaling' },
    { path: '/app/companion', icon: Bot, label: 'Companion' },
    { path: '/app/love-garden', icon: Flower, label: 'Garden' },
    { path: '/app/community', icon: MessageCircle, label: 'Community' },
    { path: '/app/divine-guidance', icon: Stars, label: 'Divine' },
    { path: '/app/more', icon: MoreHorizontal, label: 'More' }
  ];

  // More tab items (secondary features)
  const moreTabItems = [
    { path: '/app/wall', icon: Sparkles, label: 'Love Vault' },
    { path: '/app/vision-board', icon: Image, label: 'Vision Boards' },
    { path: '/app/profile', icon: User, label: 'Profile Settings' }
  ];

  // Fetch enabled navigation tabs from admin settings
  useEffect(() => {
    const fetchEnabledTabs = async () => {
      try {
        // For MVP, use default core tabs
        // In production, this will fetch from /api/navigation/tabs
        setEnabledTabs(defaultNavItems);
      } catch (error) {
        console.error('Error fetching navigation tabs:', error);
        setEnabledTabs(defaultNavItems);
      }
    };

    fetchEnabledTabs();
  }, []);

  // Use desktop items for larger screens, default for mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset profile image error when user profile picture changes
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.profile_pic]);

  const navItems = isMobile ? (enabledTabs.length > 0 ? enabledTabs : defaultNavItems) : desktopNavItems;
  
  return (
    <>
      {/* Top Header - Fixed Centered Logo */}
      <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-rose-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-20 py-4 relative">
            {/* Centered Large Logo - Bigger dimensions */}
            <Link to="/" className="flex items-center">
              <img 
                src="/constant-love-logo.png" 
                alt="Constant Love™" 
                className="w-32 h-24 sm:w-40 sm:h-28 md:w-48 md:h-32 object-contain hover:scale-105 transition-transform"
              />
            </Link>
          
            {/* User Menu - Positioned Absolutely */}
            {user && (
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-rose-50 transition-colors relative"
                >
                  <div className={`w-8 h-8 ${user.is_admin ? 'bg-gradient-to-r from-purple-500 to-pink-600' : 'bg-gradient-to-r from-rose-400 to-rose-600'} rounded-full flex items-center justify-center text-white text-sm font-bold overflow-hidden`}>
                    {user.profile_pic && user.profile_pic !== '' && !profileImageError ? (
                      <img 
                        src={user.profile_pic} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        onError={() => setProfileImageError(true)}
                      />
                    ) : (
                      user.name?.charAt(0).toUpperCase() || 'U'
                    )}
                  </div>
                  <div className="hidden sm:block">
                    <span className="text-gray-700 font-medium text-sm">{user.name}</span>
                    {user.is_admin && (
                      <div className="text-xs bg-gradient-to-r from-purple-500 to-pink-600 text-white px-2 py-0.5 rounded-full font-bold">
                        ADMIN
                      </div>
                    )}
                  </div>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      {user.is_admin && (
                        <Link
                          to="/app/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="w-full text-left px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700 flex items-center font-bold"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Panel
                        </Link>
                      )}
                      <Link
                        to="/app/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 flex items-center"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Profile Settings
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 flex items-center"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation Tab Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-rose-200 z-50">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-6' : 'grid-cols-7'} gap-0.5`}>
            {navItems.map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                className={`flex flex-col items-center px-1 py-2 text-xs font-medium transition-all duration-300 ${
                  location.pathname === path
                    ? 'text-rose-600 bg-rose-500 text-white'
                    : 'text-gray-600 hover:text-white hover:bg-rose-500'
                }`}
              >
                <Icon className="w-5 h-5 mb-0.5" />
                <span className="leading-none text-center" style={{ fontSize: '10px' }}>{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom padding to account for fixed navigation */}
      <div className="h-20"></div>
    </>
  );
};

export default Navigation;